import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import Product from '../../../../Model/Product';
import Select from '../../../../components/Select';
import { isAIFriendEnabled, isAIFriendCreatePermissionEnabled } from '../../../../utils/postHog';
import useSessionStore from '../../../../hooks/useSessionStore';
import FeaturesList from '../FeaturesList';
import {
  InputsContainer,
  Container,
  PlanSelectionFormControl,
  Title,
} from './styles';
import texts from './texts.json';

const CHALLENGE_LABEL = '(Challenge Product)';
const HYROX_LABEL = '(Hyrox Product)';

const getProductLabel = (product, productName) => {
  if (product.isChallengeProduct) {
    return `${productName} ${CHALLENGE_LABEL}`;
  }
  if (product.isHyroxProduct) {
    return `${productName} ${HYROX_LABEL}`;
  }
  return productName;
};

const ProductSelector = ({
  selectedProduct,
  onChange,
  isReadOnly,
  className,
  products,
  title,
  selectId,
  label,
}) => {
  const { isAdmin } = useSessionStore();

  const productOptions = useMemo(() => {
    const items = [];

    /**
     * Show AI Friend options if AI Friend is enabled on posthog
     * and either the user has create permission or is an admin
     */
    const showAIFriendOptions = isAIFriendEnabled() && (isAIFriendCreatePermissionEnabled() || isAdmin);

    products.forEach((product) => {
      if (product.isAiFriendEnabled && !showAIFriendOptions) {
        return;
      }
      /*
        Only show products that are not hidden (e.g. The default free products that are used for testing). However,
        if the selected product is hidden, then we need to show it in the list so that the coach can see it in the
        contract modal.
      */
      if (!product.isHidden || selectedProduct?.id === product.id) {
        const productName = product.name || texts.unnamedProduct;
        items.push({
          value: product.id,
          label: getProductLabel(product, productName),
        });
      }
    });

    return items;
  }, [
    products,
    selectedProduct,
    isAdmin,
  ]);

  return (
    <Container className={className}>
      {!!title && (<Title>{title}</Title>)}
      <InputsContainer>
        <PlanSelectionFormControl>
          <Select
            value={selectedProduct?.id || ''}
            onChange={(e) => onChange(products.find((product) => product.id === e.target.value))}
            size="small"
            MenuProps={{
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
            }}
            disabled={isReadOnly}
            options={productOptions}
            id={selectId}
            label={label}
          />
        </PlanSelectionFormControl>
      </InputsContainer>
      {!!selectedProduct && (
        <FeaturesList productDoc={selectedProduct} />
      )}
    </Container>
  );
};

ProductSelector.propTypes = {
  selectedProduct: PropTypes.instanceOf(Product),
  onChange: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
  className: PropTypes.string,
  products: PropTypes.arrayOf(PropTypes.instanceOf(Product)),
  title: PropTypes.string,
  selectId: PropTypes.string,
  label: PropTypes.string,
};

ProductSelector.defaultProps = {
  selectedProduct: null,
  isReadOnly: false,
  className: '',
  products: [],
  title: '',
  selectId: 'product-selector',
  label: '',
};

export default ProductSelector;
